import { Form, FormikProvider, useFormik } from 'formik'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@mui/material'
import React, { useState } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import sendMessageService from 'shared/services/sendMessageService'
import VivaldiIcon from 'assets/images/browserIcons/vivaldi.png'
import FirefoxIcon from 'assets/images/browserIcons/firefox.png'
import ChromeIcon from 'assets/images/browserIcons/chrome.png'
import OperaIcon from 'assets/images/browserIcons/opera.png'
import BraveIcon from 'assets/images/browserIcons/brave.png'
import EdgeIcon from 'assets/images/browserIcons/edge.png'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'
import MeetsIcon from 'assets/images/meet-icon.png'

import { EmailOutlined, LocalPhoneOutlined } from '@mui/icons-material'
import { StyledTypography, StyledContactLink, StyledLink } from './styles'
import { pl, en, de } from './locales'
import schema from './schema'

const extensions = [
   {
      icon: ChromeIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: FirefoxIcon,
      link: 'https://addons.mozilla.org/pl/firefox/addon/euromaster/',
   },
   {
      icon: EdgeIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: OperaIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: VivaldiIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
   {
      icon: BraveIcon,
      link: 'https://chromewebstore.google.com/detail/euromaster/jbleagdegjmfcadakjlommmgndijpobb',
   },
]

const contacts = [
   {
      name: 'Wiktor',
      link: 'https://meet.google.com/kqb-szqf-ccs',
   },
   {
      name: 'Mariusz',
      link: 'https://meet.google.com/att-kzqm-mgs',
   },
   {
      name: 'Paweł',
      link: 'https://meet.google.com/abf-hffk-owq',
   },
]

export default function Contact() {
   const { t, i18n } = useTranslation('CONTACT')
   i18n.addResourceBundle('pl-PL', 'CONTACT', pl)
   i18n.addResourceBundle('en-EN', 'CONTACT', en)
   i18n.addResourceBundle('de-DE', 'CONTACT', de)
   const [isLoading, setIsLoading] = useState(false)
   const { addNotification } = useNotificationContext()

   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

   const formik = useFormik<ContactType>({
      initialValues: {
         message: '',
         typeMessage: 'Recommend a friend',
      },
      validationSchema: schema,
      onSubmit: handleSubmit,
   })

   const { values, setValues } = formik

   function handleSubmit() {
      setIsLoading(true)
      const payload: ContactType = {
         message: values.message,
         typeMessage: values.typeMessage,
      }

      sendMessageService(payload)
         .then((res) => {
            if (res.status === 204) {
               addNotification('successSend', 'success')
               setValues({ message: '', typeMessage: '' })
            }
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSend', 'error')
            } else {
               addNotification('failedSend', 'error')
            }
         })
         .finally(() => setIsLoading(false))
   }

   return (
      <Box
         sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            height: '50%',
            transform: { md: 'translateY(30%)' },
         }}
      >
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               pt: 3,
               width: { md: '50%', xs: '100%' },
               textAlign: 'center',
               color: theme.palette.primary.main,
            }}
         >
            <Typography variant="h3">{t('anyProblems')}</Typography>
            <Typography variant="h5" sx={{ color: 'grayText', marginBottom: 1 }}>
               {t('contactWithUs')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
               <StyledTypography variant="body1">
                  <LocalPhoneOutlined style={{ color: theme.palette.primary.main, marginBottom: -4, marginRight: 4 }} />
                  <StyledContactLink sx={{ fontSize: 15 }} href="tel:+48507425105">
                     Tel: 507 425 105
                  </StyledContactLink>
               </StyledTypography>
               <StyledTypography variant="body1">
                  <EmailOutlined style={{ color: theme.palette.primary.main, marginBottom: -4, marginRight: 4 }} />
                  <StyledContactLink sx={{ fontSize: 15 }} href="mailto:kontakt@motorro.pl">
                     kontakt@motorro.pl
                  </StyledContactLink>
               </StyledTypography>
               <StyledTypography variant="body1">
                  {contacts.map((item) => (
                     <Box key={item.name}>
                        <img height={12} style={{ marginRight: 4 }} width="auto" src={MeetsIcon} alt="chrome" />
                        <StyledContactLink sx={{ fontSize: 15 }} href={item.link} target="_blank">
                           {t('remoteHelp', { name: item.name })}
                        </StyledContactLink>
                     </Box>
                  ))}
               </StyledTypography>
            </Box>
            {!isMobile && (
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="h5" sx={{ mt: 2 }}>
                     {t('extensionDownload')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                     {extensions.map((item) => (
                        <IconButton key={item.icon} onClick={() => window.open(item.link, '_blank')}>
                           <img height={30} width="auto" src={item.icon} alt="chrome" />
                        </IconButton>
                     ))}
                  </Box>
               </Box>
            )}
         </Box>
         <Box
            sx={{
               width: { md: '50%', xs: '100%' },
               px: '16px',
               display: 'flex',
               mt: { xs: 3 },
               alignItems: 'center',
               flexDirection: 'column',
            }}
         >
            <FormikProvider value={formik}>
               <Form>
                  <Typography
                     variant="h3"
                     textAlign="center"
                     sx={{ color: theme.palette.primary.main, marginBottom: 1 }}
                  >
                     {t('contactForm')}
                  </Typography>
                  <Stack direction="column" spacing={1}>
                     <SelectField
                        noMinWidth
                        inputVariant="outlined"
                        name="typeMessage"
                        label={t('messageType')}
                        options={[
                           { name: t('reportTypes.recommend'), value: 'Recommend a friend' },
                           { name: t('reportTypes.application'), value: 'System functionality problems' },
                           { name: t('reportTypes.suggestions'), value: 'Suggestions' },
                        ]}
                     />
                     <InputField fullWidth type="text" name="message" multiline rows={6} label={t('message')} />
                     <Typography textAlign="center" variant="body1" fontWeight="bold">
                        {t('bySending')}
                        <StyledLink to="https://smr.euromaster.pl/privacy-policy/" target="_blank">
                           {t('privacyPolicy')}{' '}
                        </StyledLink>
                        {t('and')}{' '}
                        <StyledLink to="https://smr.euromaster.pl/terms-of-use/" target="_blank">
                           {t('termsOfUse')}
                        </StyledLink>
                     </Typography>
                     <ContainedButton
                        disabled={isLoading}
                        fullWidth
                        type="submit"
                        variant="contained"
                        sx={{ height: 30, fontSize: 14 }}
                     >
                        {t('send')}
                     </ContainedButton>
                  </Stack>
               </Form>
            </FormikProvider>
         </Box>
      </Box>
   )
}
